import apiClient from '@/lib/axios';
import type {
  PageContentDto,
  PageListDto,
  NavigationDto,
  ApiResponse,
  PaginatedResponse,
} from '@/types';

function appendLang(url: string, lang?: string): string {
  if (!lang || lang === 'en') return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${lang}`;
}

/**
 * CMS API Service
 * Methods for interacting with the CMS backend
 */
export const cmsApi = {
  /**
   * Get a published page by slug with all blocks
   * @param slug - Page slug (e.g., 'home', 'about', 'pricing')
   * @param lang - Language code (e.g., 'fr', 'es')
   */
  async getPage(slug: string, lang?: string): Promise<PageContentDto> {
    const url = appendLang(`/cms/content/pages/${slug}`, lang);
    const response = await apiClient.get<ApiResponse<PageContentDto>>(url);
    return response.data.data;
  },

  /**
   * Get list of published pages with pagination and filtering
   * @param options - Query options
   */
  async getPages(options?: {
    pageType?: string;
    categorySlug?: string;
    page?: number;
    pageSize?: number;
    lang?: string;
  }): Promise<PaginatedResponse<PageListDto>> {
    const params = new URLSearchParams();
    if (options?.pageType) params.append('pageType', options.pageType);
    if (options?.categorySlug) params.append('categorySlug', options.categorySlug);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.lang && options.lang !== 'en') params.append('lang', options.lang);

    const response = await apiClient.get<PaginatedResponse<PageListDto>>(
      `/cms/content/pages?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get hierarchical navigation structure by menu slug
   * @param menuSlug - Navigation menu slug (e.g., 'primary-nav', 'footer-nav')
   * @param lang - Language code (e.g., 'fr', 'es')
   */
  async getNavigation(menuSlug: string, lang?: string): Promise<NavigationDto[]> {
    const url = appendLang(`/cms/content/navigation/${menuSlug}`, lang);
    const response = await apiClient.get<ApiResponse<NavigationDto[]>>(url);
    return response.data.data;
  },

  /**
   * Get sitemap (for SEO)
   */
  async getSitemap(): Promise<string> {
    const response = await apiClient.get<string>('/cms/content/sitemap.xml', {
      headers: {
        'Accept': 'application/xml',
      },
    });
    return response.data;
  },
};

export default cmsApi;
